import { keyframes } from '@emotion/core';
import React, { useEffect } from 'react';

import UserName from '~/components/Client/UserName';
import { showToast } from '~/components/Shared/EcToast/showToast';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import { DateText } from '~/eds/src/components/DateText/DateText';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { ERROR, SUCCESS } from '~/types/toast.types';
import Box from '~/ui/components/Box/Box';
import EditableText from '~/ui/components/EditableText/EditableText';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Switch from '~/ui/components/Switch/Switch';
import Text from '~/ui/components/Text/Text';
import { useHover } from '~/ui/hooks/interaction';
import theme from '~/ui/theme';

const fadeKeyframe = keyframes({
  from: {
    backgroundColor: theme.colors['blue-100'],
  },
  to: {
    backgroundColor: 'white',
  },
});

function ApiKeyRow({ activeKey, disabled, item, shouldAnimate, removeItem }) {
  const [ref, isHovered] = useHover();
  const intl = useIntl();

  const [updateApiKey, updateResult] = api.endpoints.updateApiKey.useMutation();
  const { isLoading, isError, isSuccess } = updateResult;

  useEffect(() => {
    if (isSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'ApiKeyRow.apiKeyUpdated',
          defaultMessage: 'API Key updated.',
          description: 'Toast message when API key is successfully updated',
        }),
      );
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'ApiKeyRow.updateApiKeyFailed',
          defaultMessage: 'Update API Key failed. Please try again.',
          description: 'Toast message when API key update fails',
        }),
      );
    }
  }, [isError]);

  function editKey(id, data) {
    updateApiKey({ id, data });
  }

  function editName(id, name) {
    editKey(id, { name });
  }

  function toggleStatus(id, is_enabled) {
    editKey(id, { is_enabled });
  }

  const apiPlaceholderString = intl.formatMessage({
    id: 'ApiKeyRow.untitledApiKey',
    defaultMessage: 'Untitled API Key',
    description: 'Placeholder for API key',
  });

  return (
    <FlexLayout
      role="row"
      bg={isHovered ? 'gray-200' : 'white'}
      px={6}
      py={4}
      ref={ref}
      sx={{
        borderTop: 'border',
        height: '68px',
        animation: shouldAnimate ? `${fadeKeyframe} 2s` : 'none',
      }}
    >
      <FlexLayout
        alignItems="center"
        flexGrow="1"
        pr={4}
        sx={{ flexBasis: '300px' }}
      >
        <Box sx={{ display: 'inline-block' }}>
          <EditableText
            disabled={disabled || isLoading}
            isOutsideHover={isHovered}
            placeholder={apiPlaceholderString}
            value={item.name}
            onChange={(value) => editName(item.id, value)}
          />
        </Box>
      </FlexLayout>
      <FlexLayout
        alignItems="flex-start"
        flexDirection="column"
        justifyContent="center"
        sx={{ width: '300px' }}
      >
        <>
          <DateText
            date={item.date_created}
            format="full_datetime"
            variant="tiny"
          />
          <Text color="gray-600" variant="2xs-dense">
            &nbsp;by&nbsp;
            <UserName userId={item.user_id} />
          </Text>
        </>
      </FlexLayout>
      <FlexLayout
        alignItems="flex-start"
        flexDirection="column"
        justifyContent="center"
        sx={{ width: '300px' }}
      >
        <DateText
          date={item.date_last_accessed}
          format="full_datetime"
          variant="tiny"
        />
      </FlexLayout>
      <FlexLayout
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '220px' }}
      >
        <FlexLayout space={4}>
          <Tooltip
            tooltip={
              !disabled && !item.is_enabled && activeKey
                ? intl.formatMessage(
                    {
                      id: 'ApiKeyRow.disableActiveKeyTooltip',
                      description:
                        'Tooltip message when trying to enable an API key while another is active. Example: keyName = "Production API Key"',
                      defaultMessage:
                        'Disable currently active API Key ({keyName}) to enable this item.',
                    },
                    {
                      keyName: activeKey?.name || apiPlaceholderString,
                    },
                  )
                : null
            }
          >
            <Switch
              disabled={
                disabled || isLoading || (activeKey && activeKey.id !== item.id)
              }
              size="s"
              value={item.is_enabled}
              onChange={() => toggleStatus(item.id, !item.is_enabled)}
            />
          </Tooltip>
          <Text color="gray-900" variant="s-dense">
            {item.is_enabled
              ? intl.formatMessage({
                  id: 'ApiKeyRow.enabled',
                  defaultMessage: 'Enabled',
                  description: 'Status text for enabled API key',
                })
              : intl.formatMessage({
                  id: 'ApiKeyRow.disabled',
                  defaultMessage: 'Disabled',
                  description: 'Status text for disabled API key',
                })}
          </Text>
        </FlexLayout>
        <FlexLayout
          alignItems="center"
          justifyContent="center"
          sx={{ width: '45px' }}
        >
          {isLoading ? (
            <LoadingSpinner size="small" />
          ) : (
            isHovered &&
            !disabled && (
              <IconButton
                icon="trash"
                text="remove"
                tooltip={intl.formatMessage({
                  id: 'ApiKeyRow.removeApiKeyTooltip',
                  defaultMessage: 'Remove API Key',
                  description: 'Tooltip for removing an API key',
                })}
                onClick={() => removeItem(item)}
              />
            )
          )}
        </FlexLayout>
      </FlexLayout>
    </FlexLayout>
  );
}

export default ApiKeyRow;
